import { useAppContext } from '../context/AppContext';
import { useMemo } from 'react';

export const useTheme = () => {
  const { state } = useAppContext();
  const { theme, app } = state;

  // Memoize expensive calculations
  const themeConfig = useMemo(() => {
    // Auto mode detection
    const systemDarkMode = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
    const isDarkMode = theme.mode === 'dark' || (theme.mode === 'auto' && systemDarkMode);

    // Dynamic theme colors based on mode
    const dynamicColors = isDarkMode ? {
      backgroundColor: '#1a1a1a',
      cardBackground: '#2d2d2d',
      textColor: '#ffffff',
      textSecondaryColor: '#b0b0b0',
      borderColor: '#404040',
      searchBarColor: '#3d3d3d',
      tableHeaderColor: '#333333',
      hoverColor: '#404040',
    } : {
      backgroundColor: theme.backgroundColor,
      cardBackground: theme.cardBackground,
      textColor: theme.textColor,
      textSecondaryColor: theme.textSecondaryColor,
      borderColor: theme.borderColor,
      searchBarColor: theme.searchBarColor,
      tableHeaderColor: theme.tableHeaderColor,
      hoverColor: theme.hoverColor,
    };

    // Return theme values for easy destructuring
    return {
      // Colors (dynamic based on theme mode)
      primaryColor: theme.primaryColor,
      secondaryColor: theme.secondaryColor,
      backgroundColor: dynamicColors.backgroundColor,
      cardBackground: dynamicColors.cardBackground,
      textColor: dynamicColors.textColor,
      textSecondaryColor: dynamicColors.textSecondaryColor,
      borderColor: dynamicColors.borderColor,
      searchBarColor: dynamicColors.searchBarColor,
      tableHeaderColor: dynamicColors.tableHeaderColor,
      hoverColor: dynamicColors.hoverColor,
      linkColor: theme.linkColor,
      linkHoverColor: theme.linkHoverColor,
      
      // App settings
      appName: app.name,
      appLogo: app.logo,
      language: app.language,
      timezone: app.timezone,
      
      // Theme mode info
      isDarkMode,
      themeMode: theme.mode,
      
      // Common style objects
      styles: {
        pageContainer: {
          flexGrow: 1,
          width: '100%',
          p: { xs: 1, sm: 2, md: 4 },
          mt: 10,
          background: dynamicColors.backgroundColor,
          minHeight: '100vh'
        },
        
        card: {
          borderRadius: 3,
          boxShadow: 'none',
          border: `1px solid ${dynamicColors.borderColor}`,
          background: dynamicColors.cardBackground
        },
        
        pageTitle: {
          color: dynamicColors.textColor,
          fontSize: { xs: 24, sm: 28, md: 32 },
          fontWeight: 700
        },
        
        searchBar: {
          display: 'flex',
          alignItems: 'center',
          background: dynamicColors.searchBarColor,
          borderRadius: 2,
          px: 2,
          height: 40,
          maxWidth: 320
        },
        
        searchInput: {
          color: dynamicColors.textColor,
          fontSize: 15,
          width: 240
        },
        
        tableHeader: {
          background: dynamicColors.tableHeaderColor
        },
        
        tableHeaderCell: {
          color: dynamicColors.textSecondaryColor,
          fontWeight: 700,
          fontSize: 14
        },
        
        tableCell: {
          color: dynamicColors.textColor,
          fontWeight: 500
        },
        
        link: {
          color: theme.linkColor,
          textDecoration: 'underline',
          cursor: 'pointer',
          fontWeight: 500,
          '&:hover': { color: theme.linkHoverColor }
        },
        
        loadingContainer: {
          flexGrow: 1,
          width: '100%',
          p: { xs: 1, sm: 2, md: 4 },
          mt: 10,
          background: dynamicColors.backgroundColor,
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        },
        
        button: {
          background: theme.primaryColor,
          '&:hover': { background: theme.linkHoverColor }
        },
        
        secondaryButton: {
          borderColor: dynamicColors.textSecondaryColor,
          color: dynamicColors.textSecondaryColor,
          '&:hover': { borderColor: dynamicColors.textColor, color: dynamicColors.textColor }
        }
      }
    };
  }, [theme, app]); // Only recalculate when theme or app config changes

  return themeConfig;
};

export default useTheme; 